/**
 * Standard Progressions Data
 * 
 * Curated collection of 14 standard progressions with direct access control:
 * - 6 FREE_TIER_PROGRESSIONS: Classic progressions, no premium features
 * - 6 PREMIUM_PREVIEW_PROGRESSIONS: Showcase premium features pedagogically
 * - 2 PREMIUM_PROGRESSIONS: Exclusive to premium subscribers (hidden from free users)
 * 
 * Architecture: Direct metadata approach
 * - Each progression has accessLevel in metadata ('free' | 'premium_preview' | 'premium_full')
 * - No categorization logic needed - access level travels with the data
 * - UI reads progression.metadata.accessLevel directly for filtering/badges
 * 
 * Adding new progressions:
 * 1. Create progression with metadata.accessLevel set
 * 2. Add to appropriate array (FREE_TIER_PROGRESSIONS, PREMIUM_PREVIEW_PROGRESSIONS, or PREMIUM_PROGRESSIONS)
 * 3. That's it - no mapping files or categorization needed
 */

import { Progression, DifficultyLevel, GenreCategory, Scale, Note, OverlayQuality, CagedQuality, DEFAULT_CAGED_VISUALIZATIONS, AccessLevel } from './presetSchema';

/**
 * Version number for standard progressions - increment when making changes
 * This helps invalidate caches when progressions are updated
 */
export const STANDARD_PROGRESSIONS_VERSION = '2.3.3';

/**
 * Curated progressions for free and premium preview tiers
 * Carefully selected to showcase app features while teaching essential progressions
 */

// =============== FREE TIER PROGRESSIONS (6) ===============
// Pure free progressions with no premium features - perfect for beginners

const FREE_TIER_PROGRESSIONS: Progression[] = [
  {
    id: 'free_pop_ballad',
    name: 'Pop Ballad (vi-IV-I-V)',
    source: 'standard',
    metadata: {
      pattern: 'vi IV I V',
      key: 'C Major',
      description: 'The most popular progression in modern pop music',
      examples: ['Someone Like You', 'Don\'t Stop Believin\'', 'Let It Be'],
      difficulty: 'beginner' as DifficultyLevel,
      genre: 'pop' as GenreCategory,
      accessLevel: 'free' as AccessLevel,
      created: Date.now(),
      modified: Date.now(),
      guitarCount: 4,
      version: '2.0.0',
      usageCount: 0,
      favorite: true,
      tags: ['popular', 'beginner', 'essential']
    },
    arrangements: [
      {
        id: 'arr_am_free_pop',
        root: {
          note: 'A' as Note,
          scale: 'MINOR' as Scale,
          showScaleDegrees: false
        },
        caged: {
          activeShapes: {A: true},
          quality: 'MINOR' as CagedQuality,
          visualizations: DEFAULT_CAGED_VISUALIZATIONS,
        },
      },
      {
        id: 'arr_f_free_pop',
        root: {
          note: 'F' as Note,
          scale: 'MAJOR' as Scale,
          showScaleDegrees: false
        },
        caged: {
          activeShapes: {E: true},
          quality: 'MAJOR' as CagedQuality,
          visualizations: DEFAULT_CAGED_VISUALIZATIONS,
        },
      },
      {
        id: 'arr_c_free_pop',
        root: {
          note: 'C' as Note,
          scale: 'MAJOR' as Scale,
          showScaleDegrees: false
        },
        caged: {
          activeShapes: {C: true},
          quality: 'MAJOR' as CagedQuality,
          visualizations: DEFAULT_CAGED_VISUALIZATIONS,
        },
      },
      {
        id: 'arr_g_free_pop',
        root: {
          note: 'G' as Note,
          scale: 'MAJOR' as Scale,
          showScaleDegrees: false
        },
        caged: {
          activeShapes: {G: true},
          quality: 'MAJOR' as CagedQuality,
          visualizations: DEFAULT_CAGED_VISUALIZATIONS,
        },
      },
    ]
  },

  {
    id: 'free_pop_punk',
    name: 'Pop Punk Classic (I-V-vi-IV)',
    source: 'standard',
    metadata: {
      pattern: 'I V vi IV',
      key: 'G Major',
      description: 'Essential pop-punk and alternative rock progression',
      examples: ['Basket Case', 'What\'s My Age Again?', 'Mr. Brightside'],
      difficulty: 'beginner' as DifficultyLevel,
      genre: 'rock' as GenreCategory,
      accessLevel: 'free' as AccessLevel,
      created: Date.now(),
      modified: Date.now(),
      guitarCount: 4,
      version: '2.0.0',
      usageCount: 0,
      favorite: false,
      tags: ['punk', 'rock', 'strumming']
    },
    arrangements: [
      {
        id: 'arr_g_free_punk',
        root: {
          note: 'G' as Note,
          scale: 'MAJOR' as Scale,
          showScaleDegrees: false
        },
        caged: {
          activeShapes: {G: true},
          quality: 'MAJOR' as CagedQuality,
          visualizations: DEFAULT_CAGED_VISUALIZATIONS,
        },
      },
      {
        id: 'arr_d_free_punk',
        root: {
          note: 'D' as Note,
          scale: 'MAJOR' as Scale,
          showScaleDegrees: false
        },
        caged: {
          activeShapes: {D: true},
          quality: 'MAJOR' as CagedQuality,
          visualizations: DEFAULT_CAGED_VISUALIZATIONS,
        },
      },
      {
        id: 'arr_em_free_punk',
        root: {
          note: 'E' as Note,
          scale: 'MINOR' as Scale,
          showScaleDegrees: false
        },
        caged: {
          activeShapes: {E: true},
          quality: 'MINOR' as CagedQuality,
          visualizations: DEFAULT_CAGED_VISUALIZATIONS,
        },
      },
      {
        id: 'arr_c_free_punk',
        root: {
          note: 'C' as Note,
          scale: 'MAJOR' as Scale,
          showScaleDegrees: false
        },
        caged: {
          activeShapes: {C: true},
          quality: 'MAJOR' as CagedQuality,
          visualizations: DEFAULT_CAGED_VISUALIZATIONS,
        },
      },
    ]
  },

  {
    id: 'free_12_bar_blues',
    name: '12-Bar Blues Foundation',
    source: 'standard',
    metadata: {
      pattern: 'I7 IV7 V7',
      key: 'A Major',
      description: 'The foundation of blues music - every guitarist needs this',
      examples: ['Sweet Home Chicago', 'Pride and Joy', 'Crossroads'],
      difficulty: 'intermediate' as DifficultyLevel,
      genre: 'blues' as GenreCategory,
      accessLevel: 'free' as AccessLevel,
      created: Date.now(),
      modified: Date.now(),
      guitarCount: 3,
      version: '2.0.0',
      usageCount: 0,
      favorite: true,
      tags: ['blues', 'classic', 'dominant']
    },
    arrangements: [
      {
        id: 'arr_a7_free_blues',
        root: {
          note: 'A' as Note,
          scale: 'MAJOR' as Scale,
          showScaleDegrees: false
        },
        caged: {
          activeShapes: {A: true},
          quality: 'MAJOR' as CagedQuality,
          visualizations: DEFAULT_CAGED_VISUALIZATIONS,
        },
      },
      {
        id: 'arr_d7_free_blues',
        root: {
          note: 'D' as Note,
          scale: 'MAJOR' as Scale,
          showScaleDegrees: false
        },
        caged: {
          activeShapes: {D: true},
          quality: 'MAJOR' as CagedQuality,
          visualizations: DEFAULT_CAGED_VISUALIZATIONS,
        },
      },
      {
        id: 'arr_e7_free_blues',
        root: {
          note: 'E' as Note,
          scale: 'MAJOR' as Scale,
          showScaleDegrees: false
        },
        caged: {
          activeShapes: {E: true},
          quality: 'MAJOR' as CagedQuality,
          visualizations: DEFAULT_CAGED_VISUALIZATIONS,
        },
      },
    ]
  },

  {
    id: 'free_campfire_folk',
    name: 'Campfire Folk Sing-Along',
    source: 'standard',
    metadata: {
      pattern: 'G C Am D',
      key: 'G Major',
      description: 'Perfect for campfire sing-alongs and acoustic sessions',
      examples: ['Wonderwall', 'Country Roads', 'Good Riddance'],
      difficulty: 'beginner' as DifficultyLevel,
      genre: 'folk' as GenreCategory,
      accessLevel: 'free' as AccessLevel,
      created: Date.now(),
      modified: Date.now(),
      guitarCount: 4,
      version: '2.0.0',
      usageCount: 0,
      favorite: false,
      tags: ['campfire', 'acoustic', 'singalong']
    },
    arrangements: [
      {
        id: 'arr_g_free_campfire',
        root: {
          note: 'G' as Note,
          scale: 'MAJOR' as Scale,
          showScaleDegrees: false
        },
        caged: {
          activeShapes: {G: true},
          quality: 'MAJOR' as CagedQuality,
          visualizations: DEFAULT_CAGED_VISUALIZATIONS,
        },
      },
      {
        id: 'arr_c_free_campfire',
        root: {
          note: 'C' as Note,
          scale: 'MAJOR' as Scale,
          showScaleDegrees: false
        },
        caged: {
          activeShapes: {C: true},
          quality: 'MAJOR' as CagedQuality,
          visualizations: DEFAULT_CAGED_VISUALIZATIONS,
        },
      },
      {
        id: 'arr_am_free_campfire',
        root: {
          note: 'A' as Note,
          scale: 'MINOR' as Scale,
          showScaleDegrees: false
        },
        caged: {
          activeShapes: {A: true},
          quality: 'MINOR' as CagedQuality,
          visualizations: DEFAULT_CAGED_VISUALIZATIONS,
        },
      },
      {
        id: 'arr_d_free_campfire',
        root: {
          note: 'D' as Note,
          scale: 'MAJOR' as Scale,
          showScaleDegrees: false
        },
        caged: {
          activeShapes: {D: true},
          quality: 'MAJOR' as CagedQuality,
          visualizations: DEFAULT_CAGED_VISUALIZATIONS,
        },
      },
    ]
  },

  {
    id: 'free_minor_progression',
    name: 'Essential Minor Progression',
    source: 'standard',
    metadata: {
      pattern: 'Am Dm Em',
      key: 'A Minor',
      description: 'Classic minor progression perfect for fingerpicking',
      examples: ['House of the Rising Sun', 'Scarborough Fair', 'Mad World'],
      difficulty: 'beginner' as DifficultyLevel,
      genre: 'folk' as GenreCategory,
      accessLevel: 'free' as AccessLevel,
      created: Date.now(),
      modified: Date.now(),
      guitarCount: 3,
      version: '2.0.0',
      usageCount: 0,
      favorite: false,
      tags: ['minor', 'fingerpicking', 'emotional']
    },
    arrangements: [
      {
        id: 'arr_am_free_minor',
        root: {
          note: 'A' as Note,
          scale: 'MINOR' as Scale,
          showScaleDegrees: false
        },
        caged: {
          activeShapes: {A: true},
          quality: 'MINOR' as CagedQuality,
          visualizations: DEFAULT_CAGED_VISUALIZATIONS,
        },
      },
      {
        id: 'arr_dm_free_minor',
        root: {
          note: 'D' as Note,
          scale: 'MINOR' as Scale,
          showScaleDegrees: false
        },
        caged: {
          activeShapes: {D: true},
          quality: 'MINOR' as CagedQuality,
          visualizations: DEFAULT_CAGED_VISUALIZATIONS,
        },
      },
      {
        id: 'arr_em_free_minor',
        root: {
          note: 'E' as Note,
          scale: 'MINOR' as Scale,
          showScaleDegrees: false
        },
        caged: {
          activeShapes: {E: true},
          quality: 'MINOR' as CagedQuality,
          visualizations: DEFAULT_CAGED_VISUALIZATIONS,
        },
      },
    ]
  },

  {
    id: 'free_circle_progression',
    name: 'Circle of Fifths Classic',
    source: 'standard',
    metadata: {
      pattern: 'C Am F G',
      key: 'C Major',
      description: 'The circle progression that appears in countless songs',
      examples: ['Stand By Me', 'Every Breath You Take', 'Blue Moon'],
      difficulty: 'beginner' as DifficultyLevel,
      genre: 'pop' as GenreCategory,
      accessLevel: 'free' as AccessLevel,
      created: Date.now(),
      modified: Date.now(),
      guitarCount: 4,
      version: '2.0.0',
      usageCount: 0,
      favorite: false,
      tags: ['circle', 'classic', 'theory']
    },
    arrangements: [
      {
        id: 'arr_c_free_circle',
        root: {
          note: 'C' as Note,
          scale: 'MAJOR' as Scale,
          showScaleDegrees: false
        },
        caged: {
          activeShapes: {C: true},
          quality: 'MAJOR' as CagedQuality,
          visualizations: DEFAULT_CAGED_VISUALIZATIONS,
        },
      },
      {
        id: 'arr_am_free_circle',
        root: {
          note: 'A' as Note,
          scale: 'MINOR' as Scale,
          showScaleDegrees: false
        },
        caged: {
          activeShapes: {A: true},
          quality: 'MINOR' as CagedQuality,
          visualizations: DEFAULT_CAGED_VISUALIZATIONS,
        },
      },
      {
        id: 'arr_f_free_circle',
        root: {
          note: 'F' as Note,
          scale: 'MAJOR' as Scale,
          showScaleDegrees: false
        },
        caged: {
          activeShapes: {E: true},
          quality: 'MAJOR' as CagedQuality,
          visualizations: DEFAULT_CAGED_VISUALIZATIONS,
        },
      },
      {
        id: 'arr_g_free_circle',
        root: {
          note: 'G' as Note,
          scale: 'MAJOR' as Scale,
          showScaleDegrees: false
        },
        caged: {
          activeShapes: {G: true},
          quality: 'MAJOR' as CagedQuality,
          visualizations: DEFAULT_CAGED_VISUALIZATIONS,
        },
      },
    ]
  },
];

// =============== PREMIUM PREVIEW PROGRESSIONS (6) ===============
// Strategic use of premium features to enhance learning and showcase capabilities

const PREMIUM_PREVIEW_PROGRESSIONS: Progression[] = [
  {
    id: 'preview_jazz_ii_v_i',
    name: 'Jazz ii-V-I with Theory',
    source: 'standard',
    metadata: {
      pattern: 'ii7 V7 Imaj7',
      key: 'C Major',
      description: 'The most important jazz progression with scale theory visualization',
      examples: ['All The Things You Are', 'Autumn Leaves', 'Fly Me to the Moon'],
      difficulty: 'advanced' as DifficultyLevel,
      genre: 'jazz' as GenreCategory,
      accessLevel: 'premium_preview' as AccessLevel,
      created: Date.now(),
      modified: Date.now(),
      guitarCount: 3,
      version: '2.0.0',
      usageCount: 0,
      favorite: true,
      tags: ['jazz', 'theory', 'modal']
    },
    arrangements: [
      {
        id: 'arr_dm7_preview_jazz',
        root: {
          note: 'D' as Note,
          scale: 'DORIAN' as Scale,
          showScaleDegrees: true // Premium: Scale degrees shown
        },
        overlay: {
          note: 'D' as Note,
          quality: 'MINOR_7TH' as OverlayQuality,
          visualizations: { GLOW: true, GROW: false, PULSE: false }, // Premium: Overlay visualization
        },
        caged: {
          activeShapes: {D: true},
          quality: 'MINOR' as CagedQuality,
          visualizations: DEFAULT_CAGED_VISUALIZATIONS,
        },
      },
      {
        id: 'arr_g7_preview_jazz',
        root: {
          note: 'G' as Note,
          scale: 'MIXOLYDIAN' as Scale,
          showScaleDegrees: true // Premium: Scale degrees shown
        },
        overlay: {
          note: 'G' as Note,
          quality: 'DOMINANT_7TH' as OverlayQuality,
          visualizations: { GLOW: true, GROW: true, PULSE: false }, // Premium: Overlay visualization
        },
        caged: {
          activeShapes: {G: true},
          quality: 'MAJOR' as CagedQuality,
          visualizations: DEFAULT_CAGED_VISUALIZATIONS,
        },
      },
      {
        id: 'arr_cmaj7_preview_jazz',
        root: {
          note: 'C' as Note,
          scale: 'MAJOR' as Scale,
          showScaleDegrees: false // Keep one free for comparison
        },
        caged: {
          activeShapes: {C: true},
          quality: 'MAJOR' as CagedQuality,
          visualizations: DEFAULT_CAGED_VISUALIZATIONS,
        },
      },
    ]
  },

  {
    id: 'preview_blues_overlay',
    name: 'Blues with Chord Tone Highlighting',
    source: 'standard',
    metadata: {
      pattern: 'A7 D7 E7',
      key: 'A Major',
      description: 'Classic blues with chord tone overlays to show the important notes',
      examples: ['Stormy Monday', 'The Thrill Is Gone', 'Sweet Home Chicago'],
      difficulty: 'intermediate' as DifficultyLevel,
      genre: 'blues' as GenreCategory,
      accessLevel: 'premium_preview' as AccessLevel,
      created: Date.now(),
      modified: Date.now(),
      guitarCount: 3,
      version: '2.0.0',
      usageCount: 0,
      favorite: false,
      tags: ['blues', 'chord-tones', 'overlay']
    },
    arrangements: [
      {
        id: 'arr_a7_preview_blues',
        root: {
          note: 'A' as Note,
          scale: 'BLUES' as Scale,
          showScaleDegrees: false
        },
        overlay: {
          note: 'A' as Note,
          quality: 'DOMINANT_7TH' as OverlayQuality,
          visualizations: { GLOW: true, GROW: false, PULSE: false }, // Premium: Shows dominant 7th chord tones
        },
        caged: {
          activeShapes: {A: true},
          quality: 'MAJOR' as CagedQuality,
          visualizations: DEFAULT_CAGED_VISUALIZATIONS,
        },
      },
      {
        id: 'arr_d7_preview_blues',
        root: {
          note: 'D' as Note,
          scale: 'MAJOR' as Scale,
          showScaleDegrees: false
        },
        caged: {
          activeShapes: {D: true},
          quality: 'MAJOR' as CagedQuality,
          visualizations: DEFAULT_CAGED_VISUALIZATIONS,
        },
      },
      {
        id: 'arr_e7_preview_blues',
        root: {
          note: 'E' as Note,
          scale: 'MAJOR' as Scale,
          showScaleDegrees: false
        },
        overlay: {
          note: 'E' as Note,
          quality: 'DOMINANT_7TH' as OverlayQuality,
          visualizations: { GLOW: true, GROW: false, PULSE: true }, // Premium: Shows dominant 7th chord tones
        },
        caged: {
          activeShapes: {E: true},
          quality: 'MAJOR' as CagedQuality,
          visualizations: DEFAULT_CAGED_VISUALIZATIONS,
        },
      },
    ]
  },

  {
    id: 'preview_neosoul_voicings',
    name: 'Neo-Soul Multiple Voicings',
    source: 'standard',
    metadata: {
      pattern: 'Cmaj7 Am7 Dm7 G7',
      key: 'C Major',
      description: 'Smooth neo-soul progression showing different chord voicings',
      examples: ['D\'Angelo', 'Erykah Badu', 'FKJ'],
      difficulty: 'advanced' as DifficultyLevel,
      genre: 'jazz' as GenreCategory,
      accessLevel: 'premium_preview' as AccessLevel,
      created: Date.now(),
      modified: Date.now(),
      guitarCount: 4,
      version: '2.0.0',
      usageCount: 0,
      favorite: false,
      tags: ['neo-soul', 'voicings', 'modern']
    },
    arrangements: [
      {
        id: 'arr_cmaj7_preview_neo',
        root: {
          note: 'C' as Note,
          scale: 'MAJOR' as Scale,
          showScaleDegrees: false
        },
        caged: {
          activeShapes: {C: true, A: true}, // Premium: Multiple CAGED shapes for different voicings
          quality: 'MAJOR' as CagedQuality,
          visualizations: DEFAULT_CAGED_VISUALIZATIONS,
        },
      },
      {
        id: 'arr_am7_preview_neo',
        root: {
          note: 'A' as Note,
          scale: 'MINOR' as Scale,
          showScaleDegrees: false
        },
        overlay: {
          note: 'A' as Note,
          quality: 'MINOR_7TH' as OverlayQuality,
          visualizations: { GLOW: true, GROW: false, PULSE: false }, // Premium: Subtle overlay for 7th chord
        },
        caged: {
          activeShapes: {A: true},
          quality: 'MINOR' as CagedQuality,
          visualizations: DEFAULT_CAGED_VISUALIZATIONS,
        },
      },
      {
        id: 'arr_dm7_preview_neo',
        root: {
          note: 'D' as Note,
          scale: 'MINOR' as Scale,
          showScaleDegrees: false
        },
        caged: {
          activeShapes: {D: true},
          quality: 'MINOR' as CagedQuality,
          visualizations: DEFAULT_CAGED_VISUALIZATIONS,
        },
      },
      {
        id: 'arr_g7_preview_neo',
        root: {
          note: 'G' as Note,
          scale: 'MAJOR' as Scale,
          showScaleDegrees: false
        },
        caged: {
          activeShapes: {G: true},
          quality: 'MAJOR' as CagedQuality,
          visualizations: DEFAULT_CAGED_VISUALIZATIONS,
        },
      },
    ]
  },

  {
    id: 'preview_modal_rock',
    name: 'Modal Rock Progression',
    source: 'standard',
    metadata: {
      pattern: 'Am G F G',
      key: 'A Dorian',
      description: 'Rock progression using Dorian mode with modal visualization',
      examples: ['Scarred Old Cedar', 'So What', 'Oye Como Va'],
      difficulty: 'intermediate' as DifficultyLevel,
      genre: 'rock' as GenreCategory,
      accessLevel: 'premium_preview' as AccessLevel,
      created: Date.now(),
      modified: Date.now(),
      guitarCount: 4,
      version: '2.0.0',
      usageCount: 0,
      favorite: false,
      tags: ['modal', 'rock', 'dorian']
    },
    arrangements: [
      {
        id: 'arr_am_preview_modal',
        root: {
          note: 'A' as Note,
          scale: 'DORIAN' as Scale, // Premium: Modal scale
          showScaleDegrees: true // Premium: Scale degrees to understand modal character
        },
        overlay: {
          note: 'A' as Note,
          quality: 'MINOR_TRIAD' as OverlayQuality,
          visualizations: { GLOW: true, GROW: false, PULSE: false }, // Premium: Subtle overlay to highlight modal center
        },
        caged: {
          activeShapes: {A: true},
          quality: 'MINOR' as CagedQuality,
          visualizations: DEFAULT_CAGED_VISUALIZATIONS,
        },
      },
      {
        id: 'arr_g_preview_modal',
        root: {
          note: 'G' as Note,
          scale: 'MAJOR' as Scale,
          showScaleDegrees: false
        },
        caged: {
          activeShapes: {G: true},
          quality: 'MAJOR' as CagedQuality,
          visualizations: DEFAULT_CAGED_VISUALIZATIONS,
        },
      },
      {
        id: 'arr_f_preview_modal',
        root: {
          note: 'F' as Note,
          scale: 'MAJOR' as Scale,
          showScaleDegrees: false
        },
        caged: {
          activeShapes: {E: true},
          quality: 'MAJOR' as CagedQuality,
          visualizations: DEFAULT_CAGED_VISUALIZATIONS,
        },
      },
      {
        id: 'arr_g2_preview_modal',
        root: {
          note: 'G' as Note,
          scale: 'MAJOR' as Scale,
          showScaleDegrees: false
        },
        caged: {
          activeShapes: {G: true},
          quality: 'MAJOR' as CagedQuality,
          visualizations: DEFAULT_CAGED_VISUALIZATIONS,
        },
      },
    ]
  },

  {
    id: 'preview_bossa_nova',
    name: 'Bossa Nova Harmony',
    source: 'standard',
    metadata: {
      pattern: 'Cmaj7 A7 Dm7 G7',
      key: 'C Major',
      description: 'Smooth bossa nova progression with subtle chord extension overlays',
      examples: ['Girl from Ipanema', 'Corcovado', 'Wave'],
      difficulty: 'advanced' as DifficultyLevel,
      genre: 'jazz' as GenreCategory,
      accessLevel: 'premium_preview' as AccessLevel,
      created: Date.now(),
      modified: Date.now(),
      guitarCount: 4,
      version: '2.0.0',
      usageCount: 0,
      favorite: false,
      tags: ['bossa-nova', 'brazilian', 'extensions']
    },
    arrangements: [
      {
        id: 'arr_cmaj7_preview_bossa',
        root: {
          note: 'C' as Note,
          scale: 'MAJOR' as Scale,
          showScaleDegrees: true // Premium: Scale degrees for jazz theory
        },
        overlay: {
          note: 'C' as Note,
          quality: 'MAJOR_7TH' as OverlayQuality,
          visualizations: { GLOW: true, GROW: false, PULSE: false }, // Premium: Gentle overlay for maj7
        },
        caged: {
          activeShapes: {C: true},
          quality: 'MAJOR' as CagedQuality,
          visualizations: DEFAULT_CAGED_VISUALIZATIONS,
        },
      },
      {
        id: 'arr_a7_preview_bossa',
        root: {
          note: 'A' as Note,
          scale: 'MAJOR' as Scale,
          showScaleDegrees: false
        },
        caged: {
          activeShapes: {A: true},
          quality: 'MAJOR' as CagedQuality,
          visualizations: DEFAULT_CAGED_VISUALIZATIONS,
        },
      },
      {
        id: 'arr_dm7_preview_bossa',
        root: {
          note: 'D' as Note,
          scale: 'MINOR' as Scale,
          showScaleDegrees: false
        },
        overlay: {
          note: 'D' as Note,
          quality: 'MINOR_7TH' as OverlayQuality,
          visualizations: { GLOW: true, GROW: false, PULSE: false }, // Premium: Gentle overlay for m7
        },
        caged: {
          activeShapes: {D: true},
          quality: 'MINOR' as CagedQuality,
          visualizations: DEFAULT_CAGED_VISUALIZATIONS,
        },
      },
      {
        id: 'arr_g7_preview_bossa',
        root: {
          note: 'G' as Note,
          scale: 'MAJOR' as Scale,
          showScaleDegrees: false
        },
        caged: {
          activeShapes: {G: true},
          quality: 'MAJOR' as CagedQuality,
          visualizations: DEFAULT_CAGED_VISUALIZATIONS,
        },
      },
    ]
  },

  {
    id: 'preview_alt_rock_power',
    name: 'Alternative Rock with Lead Overlay',
    source: 'standard',
    metadata: {
      pattern: 'Em C G D',
      key: 'E Minor',
      description: 'Alternative rock progression with pentatonic overlay for lead playing',
      examples: ['Zombie', 'Creep', 'Losing Religion'],
      difficulty: 'intermediate' as DifficultyLevel,
      genre: 'rock' as GenreCategory,
      accessLevel: 'premium_preview' as AccessLevel,
      created: Date.now(),
      modified: Date.now(),
      guitarCount: 4,
      version: '2.0.0',
      usageCount: 0,
      favorite: false,
      tags: ['alternative', 'pentatonic', 'lead']
    },
    arrangements: [
      {
        id: 'arr_em_preview_alt',
        root: {
          note: 'E' as Note,
          scale: 'PENTATONIC_MINOR' as Scale, // Premium: Pentatonic scale for lead
          showScaleDegrees: false
        },
        overlay: {
          note: 'E' as Note,
          quality: 'MINOR_TRIAD' as OverlayQuality,
          visualizations: { GLOW: true, GROW: false, PULSE: false }, // Premium: Shows power chord structure
        },
        caged: {
          activeShapes: {E: true},
          quality: 'MINOR' as CagedQuality,
          visualizations: DEFAULT_CAGED_VISUALIZATIONS,
        },
      },
      {
        id: 'arr_c_preview_alt',
        root: {
          note: 'C' as Note,
          scale: 'MAJOR' as Scale,
          showScaleDegrees: false
        },
        caged: {
          activeShapes: {C: true},
          quality: 'MAJOR' as CagedQuality,
          visualizations: DEFAULT_CAGED_VISUALIZATIONS,
        },
      },
      {
        id: 'arr_g_preview_alt',
        root: {
          note: 'G' as Note,
          scale: 'MAJOR' as Scale,
          showScaleDegrees: false
        },
        overlay: {
          note: 'G' as Note,
          quality: 'MAJOR_TRIAD' as OverlayQuality,
          visualizations: { GLOW: true, GROW: false, PULSE: false }, // Premium: Shows chord tones for soloing
        },
        caged: {
          activeShapes: {G: true},
          quality: 'MAJOR' as CagedQuality,
          visualizations: DEFAULT_CAGED_VISUALIZATIONS,
        },
      },
      {
        id: 'arr_d_preview_alt',
        root: {
          note: 'D' as Note,
          scale: 'MAJOR' as Scale,
          showScaleDegrees: false
        },
        caged: {
          activeShapes: {D: true},
          quality: 'MAJOR' as CagedQuality,
          visualizations: DEFAULT_CAGED_VISUALIZATIONS,
        },
      },
    ]
  },
];

// =============== PREMIUM PROGRESSIONS (2) ===============
// Full premium progressions - hidden from free users, exclusive to premium subscribers

const PREMIUM_PROGRESSIONS: Progression[] = [
  {
    id: 'premium_advanced_jazz',
    name: 'Advanced Jazz Reharmonization',
    source: 'standard',
    metadata: {
      pattern: 'IMaj7 bIIMaj7 ii7 V7alt',
      key: 'C Major',
      description: 'Sophisticated jazz reharmonization with tritone substitution and altered dominants',
      examples: ['All The Things You Are (reharmonized)', 'Autumn Leaves (advanced)'],
      difficulty: 'advanced' as DifficultyLevel,
      genre: 'jazz' as GenreCategory,
      accessLevel: 'premium_full' as AccessLevel,
      created: Date.now(),
      modified: Date.now(),
      guitarCount: 4,
      version: '2.0.0',
      usageCount: 0,
      favorite: false,
      tags: ['advanced', 'jazz', 'reharmonization', 'premium-exclusive']
    },
    arrangements: [
      {
        id: 'arr_cmaj7_premium',
        root: {
          note: 'C' as Note,
          scale: 'MAJOR' as Scale,
          showScaleDegrees: true  // Premium: Full scale degree analysis
        },
        overlay: {
          note: 'C' as Note,
          quality: 'MAJOR_7TH' as OverlayQuality,
          visualizations: { GLOW: true, GROW: true, PULSE: true }, // Premium: All visualizations
        },
        caged: {
          activeShapes: {C: true, A: true}, // Premium: Multiple CAGED shapes
          quality: 'MAJOR' as CagedQuality,
          visualizations: DEFAULT_CAGED_VISUALIZATIONS,
        },
      },
      {
        id: 'arr_dbmaj7_premium',
        root: {
          note: 'D' as Note,
          scale: 'MAJOR' as Scale,
          showScaleDegrees: true
        },
        overlay: {
          note: 'D' as Note,
          quality: 'MAJOR_7TH' as OverlayQuality,
          visualizations: { GLOW: true, GROW: true, PULSE: false },
        },
        caged: {
          activeShapes: {D: true, C: true},
          quality: 'MAJOR' as CagedQuality,
          visualizations: DEFAULT_CAGED_VISUALIZATIONS,
        },
      },
      {
        id: 'arr_dm7_premium',
        root: {
          note: 'D' as Note,
          scale: 'DORIAN' as Scale, // Premium: Modal harmony
          showScaleDegrees: true
        },
        overlay: {
          note: 'D' as Note,
          quality: 'MINOR_7TH' as OverlayQuality,
          visualizations: { GLOW: true, GROW: false, PULSE: true },
        },
        caged: {
          activeShapes: {D: true, A: true},
          quality: 'MINOR' as CagedQuality,
          visualizations: DEFAULT_CAGED_VISUALIZATIONS,
        },
      },
      {
        id: 'arr_g7alt_premium',
        root: {
          note: 'G' as Note,
          scale: 'OCTATONIC_HALF_WHOLE' as Scale, // Premium: Advanced jazz scale (altered dominant substitute)
          showScaleDegrees: true
        },
        overlay: {
          note: 'G' as Note,
          quality: 'DOMINANT_7TH' as OverlayQuality,
          visualizations: { GLOW: true, GROW: true, PULSE: true },
        },
        caged: {
          activeShapes: {G: true, E: true},
          quality: 'DOMINANT' as CagedQuality,
          visualizations: DEFAULT_CAGED_VISUALIZATIONS,
        },
      },
    ]
  },
  {
    id: 'premium_polyrhythmic_prog',
    name: 'Polyrhythmic Modal Progression',
    source: 'standard',
    metadata: {
      pattern: 'i7 bVII7 bVI7 V7',
      key: 'A Minor',
      description: 'Complex polyrhythmic progression with multiple modal centers and advanced overlays',
      examples: ['Tool - Lateralus', 'King Crimson - Frame By Frame'],
      difficulty: 'expert' as DifficultyLevel,
      genre: 'progressive' as GenreCategory,
      accessLevel: 'premium_full' as AccessLevel,
      created: Date.now(),
      modified: Date.now(),
      guitarCount: 4,
      version: '2.0.0',
      usageCount: 0,
      favorite: false,
      tags: ['expert', 'progressive', 'polyrhythmic', 'modal', 'premium-exclusive']
    },
    arrangements: [
      {
        id: 'arr_am7_poly',
        root: {
          note: 'A' as Note,
          scale: 'MINOR' as Scale, // Premium: Natural minor (Aeolian mode)
          showScaleDegrees: true
        },
        overlay: {
          note: 'A' as Note,
          quality: 'MINOR_7TH' as OverlayQuality,
          visualizations: { GLOW: true, GROW: true, PULSE: true },
        },
        caged: {
          activeShapes: {A: true, G: true, E: true}, // Premium: Multiple shapes
          quality: 'MINOR' as CagedQuality,
          visualizations: DEFAULT_CAGED_VISUALIZATIONS,
        },
      },
      {
        id: 'arr_g7_poly',
        root: {
          note: 'G' as Note,
          scale: 'MIXOLYDIAN' as Scale, // Premium: Modal harmony
          showScaleDegrees: true
        },
        overlay: {
          note: 'G' as Note,
          quality: 'DOMINANT_7TH' as OverlayQuality,
          visualizations: { GLOW: true, GROW: false, PULSE: true },
        },
        caged: {
          activeShapes: {G: true, E: true, D: true},
          quality: 'DOMINANT' as CagedQuality,
          visualizations: DEFAULT_CAGED_VISUALIZATIONS,
        },
      },
      {
        id: 'arr_f7_poly',
        root: {
          note: 'F' as Note,
          scale: 'LYDIAN' as Scale, // Premium: Advanced modal scale
          showScaleDegrees: true
        },
        overlay: {
          note: 'F' as Note,
          quality: 'MAJOR_7TH' as OverlayQuality,
          visualizations: { GLOW: true, GROW: true, PULSE: false },
        },
        caged: {
          activeShapes: {C: true, A: true, G: true}, // Premium: Multiple CAGED shapes
          quality: 'MAJOR' as CagedQuality,
          visualizations: DEFAULT_CAGED_VISUALIZATIONS,
        },
      },
      {
        id: 'arr_e7_poly',
        root: {
          note: 'E' as Note,
          scale: 'PHRYGIAN' as Scale, // Premium: Phrygian mode (exotic sound)
          showScaleDegrees: true
        },
        overlay: {
          note: 'E' as Note,
          quality: 'DOMINANT_7TH' as OverlayQuality,
          visualizations: { GLOW: true, GROW: true, PULSE: true },
        },
        caged: {
          activeShapes: {E: true, D: true, A: true},
          quality: 'DOMINANT' as CagedQuality,
          visualizations: DEFAULT_CAGED_VISUALIZATIONS,
        },
      },
    ]
  },
];

// =============== COMBINED CURATED PROGRESSIONS ===============
/**
 * All standard progressions: 6 free + 6 premium preview + 2 premium full
 * Each progression has accessLevel directly in metadata - no processing needed
 */
export const STANDARD_PROGRESSIONS: Progression[] = [
  ...FREE_TIER_PROGRESSIONS,
  ...PREMIUM_PREVIEW_PROGRESSIONS,
  ...PREMIUM_PROGRESSIONS,
];
